import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { accountabilityAPI } from '../../../services/accountabilityAPI';
import {
  WorkerSuspension,
  SuspensionFilters,
  Pagination,
} from '../../../types/accountability';
import {
  Table,
  Badge,
  Button,
  Card,
  Stack,
  Typography,
  Alert,
  CircularProgress,
  Box,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
import { format } from 'date-fns';
import LockOpenIcon from '@mui/icons-material/LockOpen';

export const SuspensionsPage: React.FC = () => {
  const navigate = useNavigate();
  const [suspensions, setSuspensions] = useState<WorkerSuspension[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Filters
  const [filters, setFilters] = useState<SuspensionFilters>({
    page: 1,
    per_page: 25,
  });

  // Lift suspension modal
  const [liftModal, setLiftModal] = useState<{
    open: boolean;
    suspensionId: number | null;
    reason: string;
  }>({
    open: false,
    suspensionId: null,
    reason: '',
  });

  useEffect(() => {
    loadSuspensions();
  }, [filters]);

  const loadSuspensions = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await accountabilityAPI.getSuspensions(filters);

      if (response.success) {
        setSuspensions(response.data || response.suspensions || []);
        setPagination(response.pagination || null);
      }
    } catch (err: any) {
      setError(err.response?.data?.errors?.[0] || 'Failed to load suspensions');
    } finally {
      setLoading(false);
    }
  };

  const handleLiftSuspension = async () => {
    if (!liftModal.suspensionId || !liftModal.reason) return;

    try {
      const response = await accountabilityAPI.liftSuspension(
        liftModal.suspensionId,
        liftModal.reason,
      );

      if (response.success) {
        setSuccess('Suspension lifted successfully');
        closeLiftModal();
        loadSuspensions();
      }
    } catch (err: any) {
      setError(err.response?.data?.errors?.[0] || 'Failed to lift suspension');
    }
  };

  const openLiftModal = (suspensionId: number) => {
    setLiftModal({
      open: true,
      suspensionId,
      reason: '',
    });
  };

  const closeLiftModal = () => {
    setLiftModal({
      open: false,
      suspensionId: null,
      reason: '',
    });
  };

  const getSuspensionTypeBadge = (type: string) => {
    return type === 'late_drop' ? (
      <Badge color="warning">Late Drop</Badge>
    ) : (
      <Badge color="error">No Show</Badge>
    );
  };

  const getStatusBadge = (status: string) => {
    const colors: Record<string, 'info' | 'error' | 'success' | 'default'> = {
      pending: 'info',
      active: 'error',
      completed: 'success',
      lifted: 'default',
    };

    return (
      <Badge color={colors[status] || 'default'}>{status.toUpperCase()}</Badge>
    );
  };

  const getDurationDisplay = (days: number) => {
    if (days === -1) return <Badge color="error">PERMANENT</Badge>;
    return `${days} days`;
  };

  const handlePageChange = (newPage: number) => {
    setFilters({ ...filters, page: newPage });
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack spacing={3}>
        {/* Header */}
        <Typography variant="h4">
          Worker Accountability - Suspensions
        </Typography>

        {/* Alerts */}
        {error && (
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {/* Filters Row */}
        <Card sx={{ p: 2 }}>
          <Stack direction="row" spacing={2}>
            <FormControl sx={{ minWidth: 150 }}>
              <InputLabel>Type</InputLabel>
              <Select
                value={filters.suspension_type || ''}
                label="Type"
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    suspension_type: e.target.value as any,
                  })
                }
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="late_drop">Late Drop</MenuItem>
                <MenuItem value="no_show">No Show</MenuItem>
              </Select>
            </FormControl>

            <FormControl sx={{ minWidth: 150 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.status || ''}
                label="Status"
                onChange={(e) =>
                  setFilters({ ...filters, status: e.target.value as any })
                }
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="lifted">Lifted</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </Card>

        {/* Table */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Card>
            <Table>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Worker</th>
                  <th>Type</th>
                  <th>Strike #</th>
                  <th>Duration</th>
                  <th>Start Date</th>
                  <th>End Date</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {suspensions.map((suspension) => (
                  <tr key={suspension.id}>
                    <td>{suspension.id}</td>
                    <td>
                      <Button
                        variant="text"
                        onClick={() =>
                          navigate(`/workers/${suspension.worker_id}`)
                        }
                      >
                        {suspension.worker_name}
                      </Button>
                    </td>
                    <td>
                      {getSuspensionTypeBadge(suspension.suspension_type)}
                    </td>
                    <td>{suspension.strike_number}</td>
                    <td>{getDurationDisplay(suspension.duration_days)}</td>
                    <td>
                      {format(new Date(suspension.starts_at), 'MMM dd, yyyy')}
                    </td>
                    <td>
                      {suspension.ends_at
                        ? format(new Date(suspension.ends_at), 'MMM dd, yyyy')
                        : '-'}
                    </td>
                    <td>{getStatusBadge(suspension.status)}</td>
                    <td>
                      {suspension.status === 'active' && (
                        <IconButton
                          size="small"
                          color="primary"
                          title="Lift Suspension"
                          onClick={() => openLiftModal(suspension.id)}
                        >
                          <LockOpenIcon />
                        </IconButton>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>

            {/* Pagination */}
            {pagination && (
              <Box
                sx={{
                  p: 2,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Typography variant="body2">
                  Showing{' '}
                  {(pagination.current_page - 1) * pagination.per_page + 1} to{' '}
                  {Math.min(
                    pagination.current_page * pagination.per_page,
                    pagination.total_count,
                  )}{' '}
                  of {pagination.total_count} suspensions
                </Typography>
                <Stack direction="row" spacing={1}>
                  <Button
                    disabled={pagination.current_page === 1}
                    onClick={() =>
                      handlePageChange(pagination.current_page - 1)
                    }
                  >
                    Previous
                  </Button>
                  <Typography sx={{ px: 2, py: 1 }}>
                    Page {pagination.current_page} of {pagination.total_pages}
                  </Typography>
                  <Button
                    disabled={
                      pagination.current_page === pagination.total_pages
                    }
                    onClick={() =>
                      handlePageChange(pagination.current_page + 1)
                    }
                  >
                    Next
                  </Button>
                </Stack>
              </Box>
            )}
          </Card>
        )}

        {/* Lift Suspension Modal */}
        <Dialog
          open={liftModal.open}
          onClose={closeLiftModal}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Lift Suspension</DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Reason (required)"
              value={liftModal.reason}
              onChange={(e) =>
                setLiftModal({ ...liftModal, reason: e.target.value })
              }
              sx={{ mt: 2 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={closeLiftModal}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleLiftSuspension}
              disabled={!liftModal.reason}
            >
              Lift Suspension
            </Button>
          </DialogActions>
        </Dialog>
      </Stack>
    </Box>
  );
};
